import { toast } from 'react-toastify'

/**
 * Handle image upload errors with specific, user-friendly toast notifications
 *
 * This utility parses backend error messages and displays appropriate toast notifications
 * with helpful guidance for users when their image uploads are rejected by content moderation.
 *
 * @param error - The error object from the API call
 * @param defaultMessage - Fallback message if error cannot be parsed
 */
export const handleUploadError = (error: any, defaultMessage: string = 'Upload failed') => {
  // Extract error message from various possible error structures
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    defaultMessage

  console.error('Upload error:', message)

  // Check for specific error types and show appropriate toast notifications

  // 1. NSFW / Explicit Content
  if (
    message.includes('Explicit Nudity') ||
    message.includes('NSFW') ||
    message.includes('explicit') ||
    message.includes('inappropriate content')
  ) {
    toast.error(
      '🚫 Image Rejected: Inappropriate Content',
      {
        position: 'top-center',
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    )

    toast.info(
      'NSFW content detected. You\'ll receive an email with details. Please upload family-friendly images appropriate for all ages.',
      {
        position: 'top-center',
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    )
    return
  }

  // 2. Suggestive Content
  if (message.includes('Suggestive') || message.includes('provocative')) {
    toast.error(
      '⚠️ Image Rejected: Suggestive Content',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )

    toast.info(
      'Please choose images appropriate for a general audience. Check your email for details.',
      {
        position: 'top-center',
        autoClose: 7000,
      }
    )
    return
  }

  // 3. Violence / Gore
  if (
    message.includes('Violence') ||
    message.includes('Gore') ||
    message.includes('gory') ||
    message.includes('Visually Disturbing')
  ) {
    toast.error(
      '⚠️ Image Rejected: Violent Content',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )

    toast.info(
      'Graphic violence or gore detected. Please avoid disturbing imagery in uploads.',
      {
        position: 'top-center',
        autoClose: 7000,
      }
    )
    return
  }

  // 4. Hate Symbols
  if (message.includes('Hate Symbol') || message.includes('offensive imagery')) {
    toast.error(
      '🛑 Image Rejected: Hate Symbols Detected',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )

    toast.info(
      'Zero tolerance for hate symbols or discriminatory content. This violation has been logged.',
      {
        position: 'top-center',
        autoClose: 8000,
      }
    )
    return
  }

  // 5. Malware / Security Threat
  if (
    message.includes('malware') ||
    message.includes('Security threat') ||
    message.includes('security scan')
  ) {
    toast.error(
      '🛡️ Security Threat Detected',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )

    toast.warning(
      'Your file was flagged as a potential security threat. Please use trusted, legitimate image files.',
      {
        position: 'top-center',
        autoClose: 7000,
      }
    )
    return
  }

  // 6. File Too Large
  if (message.includes('too large') || message.includes('file size') || message.includes('5MB')) {
    toast.error(
      '📦 Image Too Large (Max 5MB)',
      {
        position: 'top-center',
        autoClose: 5000,
      }
    )

    toast.info(
      'Please compress your image using tools like TinyPNG.com or reduce the resolution.',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )
    return
  }

  // 7. Invalid Dimensions
  if (message.includes('dimensions') || message.includes('too small') || message.includes('too large')) {
    toast.error(
      `📐 Invalid Image Dimensions`,
      {
        position: 'top-center',
        autoClose: 5000,
      }
    )

    toast.info(
      'Images must be between 50px and 4000px in width and height.',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )
    return
  }

  // 8. Invalid File Type
  if (
    message.includes('file type') ||
    message.includes('Invalid file') ||
    message.includes('format') ||
    message.includes('JPEG, PNG, WebP')
  ) {
    toast.error(
      '📁 Invalid File Type',
      {
        position: 'top-center',
        autoClose: 5000,
      }
    )

    toast.info(
      'Only JPEG, PNG, WebP, and GIF images are supported.',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )
    return
  }

  // 9. Generic Content Guidelines Violation
  if (
    message.includes('content guidelines') ||
    message.includes('community guidelines') ||
    message.includes('policy violation')
  ) {
    toast.error(
      '⚠️ Content Guidelines Violation',
      {
        position: 'top-center',
        autoClose: 6000,
      }
    )

    toast.info(
      'Your image doesn\'t meet our community guidelines. Check your email for details.',
      {
        position: 'top-center',
        autoClose: 7000,
      }
    )
    return
  }

  // Default: Show the actual error message
  toast.error(
    message,
    {
      position: 'top-center',
      autoClose: 5000,
    }
  )
}

/**
 * Simplified version for quick inline usage
 */
export const showUploadError = (errorMessage: string) => {
  handleUploadError({ message: errorMessage })
}
